/**
 * Confirmation dialog whose weight scales with consequence (thin-client §9):
 *   - default:  a plain confirm/cancel (reversible or low-stakes actions).
 *   - danger:   red primary action (destructive but recoverable, e.g. restore).
 *   - type-to-confirm: the confirm button stays disabled until the operator types
 *     an exact phrase (irreversible actions — hard-delete, permanent-delete).
 *
 * The busy state disables the actions while the mutation is in flight so a single
 * click can't double-fire.
 */

import { useState } from "react";
import type { ReactNode } from "react";
import { Modal } from "./Modal";

export function Confirm(props: {
  title: string;
  body: ReactNode;
  confirmLabel: string;
  tone?: "default" | "danger";
  /** When set, the confirm button unlocks only after the operator types this exactly. */
  confirmPhrase?: string;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { title, body, confirmLabel, tone = "default", confirmPhrase, busy, onConfirm, onCancel } = props;
  const [typed, setTyped] = useState("");
  const phraseOk = !confirmPhrase || typed === confirmPhrase;

  return (
    <Modal title={title} onClose={busy ? () => {} : onCancel}>
      <div className="modal-body">{body}</div>
      {confirmPhrase && (
        <label className="field">
          <span>
            Type <code className="mono">{confirmPhrase}</code> to confirm
          </span>
          <input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoComplete="off"
            spellCheck={false}
            aria-label={`Type ${confirmPhrase} to confirm`}
          />
        </label>
      )}
      <div className="modal-actions">
        <button type="button" className="btn" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button
          type="button"
          className={`btn ${tone === "danger" ? "btn--danger" : "btn--primary"}`}
          onClick={onConfirm}
          disabled={busy || !phraseOk}
        >
          {busy ? "Working…" : confirmLabel}
        </button>
      </div>
    </Modal>
  );
}
